import { makeAutoObservable, runInAction, computed } from "mobx";
import { IRootStore, ROLES } from "@shared/types";
import {
  ActivityMetricsResponse,
  SchoolActivity,
  ActivityMetrics,
  SystemOverview,
} from "@shared/types";
import {
  getUsers,
  getUserById,
  createUser,
  updateUserAdmin as updateUser,
  deleteUser,
  getSystemOverview,
  getScoreStatistics,
  getUserGrowthData,
  getMostActiveEntities,
  getActivityMetrics,
  getClasses,
  getSchoolsAndGrades,
} from "../../services/api";

// Interface for active entity with name and score count
interface ActiveEntity {
  id: string;
  name: string;
  scoreCount: number;
}

// Interface for most active entities
interface MostActiveEntities {
  mostActiveStudent?: ActiveEntity;
  mostActiveTeacher?: ActiveEntity;
  mostActiveSchool?: ActiveEntity;
}

/**
 * 👨‍💼 HYBRID ADMIN CONTROLLER - Modern Controller for System Administration
 * 
 * **Main Purpose:** Controller that manages admin UI state and business logic
 * while working WITH TanStack Query for data fetching
 * 
 * **RESPONSIBILITIES (Hybrid Architecture):**
 * ✅ UI State Management (selections, filters, school selection)
 * ✅ Complex Business Logic (multi-step operations) 
 * ✅ Computed Values (derived from UI state)
 * ✅ Validation and Rules (business rules)
 * ✅ Data Synchronization (from TanStack Query to MobX for backward compatibility)
 * ✅ Error Handling (UI-specific errors)
 * 
 * **TRANSITIONING RESPONSIBILITIES (gradually moving to TanStack Query):**
 * 🔄 Data Fetching (loadUsers, loadClasses, etc.) - being deprecated
 * 🔄 Loading States (isLoading) - prefer TanStack Query states
 * 🔄 Manual Refresh Logic - prefer TanStack Query invalidation
 * 🔄 Cache Management - handled by TanStack Query
 * 
 * **Pattern:** Hybrid UI Controller + Business Logic with TanStack Query Integration
 */
class HybridAdminController {
  rootStore: IRootStore;

  // ==================== DATA SYNC FROM TANSTACK QUERY ====================
  // These properties sync data from TanStack Query for backward compatibility
  
  comprehensiveActivityData: ActivityMetricsResponse | null = null;
  availableSchools: any[] = [];
  
  // Legacy data properties (gradually being phased out - prefer TanStack Query)
  users: any[] = [];
  currentUser: any | null = null;
  systemOverview: any | null = null;
  scoreStatistics: any | null = null;
  userGrowthData: any[] | null = null;
  mostActiveEntities: MostActiveEntities | null = null;
  activityMetrics: any | null = null;
  classes: any | null = null;

  // ==================== UI STATE MANAGEMENT ====================
  // This is what controllers should primarily manage
  
  selectedSchoolId: string | null = null;
  selectedUserIds = new Set<string>();
  selectedClassIds = new Set<string>();
  
  // Filter states
  userFilters = {
    role: 'all' as 'all' | 'student' | 'teacher' | 'admin',
    status: 'all' as 'all' | 'active' | 'inactive',
    searchTerm: '',
  };
  
  // UI preferences
  viewMode: 'list' | 'grid' = 'list';
  sortBy: 'name' | 'email' | 'created_at' = 'name';
  sortOrder: 'asc' | 'desc' = 'asc';
  
  // Business operation states
  bulkOperationInProgress = false;
  isLoadingSchools = false; // UI-specific loading state
  
  // Legacy loading states (being phased out - prefer TanStack Query)
  isLoadingActivityData = false;
  isLoading = false;
  error: string | null = null;
  private loadingOperations = new Set<string>();

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // ==================== ERROR HANDLING ====================
  
  setError = (error: string | null) => {
    this.error = error;
  };

  // ==================== COMPUTED VALUES ====================
  // Business logic derived from UI state
  
  @computed
  get hasSelectedUsers(): boolean {
    return this.selectedUserIds.size > 0;
  }

  @computed
  get selectedUsersCount(): number {
    return this.selectedUserIds.size;
  }

  @computed
  get canPerformBulkActions(): boolean {
    return this.hasSelectedUsers && !this.bulkOperationInProgress;
  }

  @computed
  get hasSelectedClasses(): boolean {
    return this.selectedClassIds.size > 0;
  }

  @computed
  get isDataAvailable(): boolean {
    return !!this.comprehensiveActivityData;
  }

  // ==================== UI STATE SETTERS ====================

  toggleUserSelection = (userId: string) => {
    if (this.selectedUserIds.has(userId)) {
      this.selectedUserIds.delete(userId);
    } else {
      this.selectedUserIds.add(userId);
    }
  };

  selectAllUsers = (userIds: string[]) => {
    userIds.forEach(id => this.selectedUserIds.add(id));
  };

  clearSelections = () => {
    this.selectedUserIds.clear();
    this.selectedClassIds.clear();
  };

  setUserFilters = (filters: Partial<typeof this.userFilters>) => {
    Object.assign(this.userFilters, filters);
  };

  setViewMode = (mode: 'list' | 'grid') => {
    this.viewMode = mode;
  };

  setSorting = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    this.sortBy = sortBy as any;
    this.sortOrder = sortOrder;
  };

  /**
   * Reset all UI state to defaults
   */
  resetToDefaults = () => {
    this.selectedSchoolId = null;
    this.clearSelections();
    this.userFilters = {
      role: 'all',
      status: 'all', 
      searchTerm: '',
    };
    this.viewMode = 'list';
    this.sortBy = 'name';
    this.sortOrder = 'asc';
  };

  // ==================== LEGACY METHODS (Gradually being phased out) ====================
  // These methods are kept for backward compatibility but should be replaced by TanStack Query
  
  /**
   * @deprecated Use TanStack Query loading states instead
   * Kept for backward compatibility during migration
   */
  setIsLoading = (isLoading: boolean) => {
    console.warn('setIsLoading is deprecated. Use TanStack Query loading states instead.');
    this.isLoading = isLoading;
  };

  /**
   * @deprecated Use TanStack Query loading states instead
   * Legacy loading state management for multiple simultaneous operations
   */
  private startLoading = (operationName: string) => {
    console.warn(`startLoading('${operationName}') is deprecated. Use TanStack Query loading states instead.`);
    this.loadingOperations.add(operationName);
    this.isLoading = true;
  };

  /**
   * @deprecated Use TanStack Query loading states instead
   */  
  private stopLoading = (operationName: string) => {
    this.loadingOperations.delete(operationName);
    // Only set isLoading to false when all operations are complete
    if (this.loadingOperations.size === 0) {
      this.isLoading = false;
    }
  };

  // ==================== BUSINESS LOGIC METHODS ====================
  // Complex operations that involve multiple steps or business rules
  
  /**
   * Complex filtering logic that might be expensive to compute
   */
  filterUsers(users: any[]): any[] {
    if (!users) return [];
    
    return users.filter(user => {
      // Role filter
      if (this.userFilters.role !== 'all' && user.role !== this.userFilters.role) {
        return false;
      }
      
      // Status filter
      if (this.userFilters.status !== 'all') {
        const isActive = user.last_login && this.isRecentActivity(user.last_login);
        if (this.userFilters.status === 'active' && !isActive) return false;
        if (this.userFilters.status === 'inactive' && isActive) return false;
      }
      
      // Search filter
      if (this.userFilters.searchTerm) {
        const searchLower = this.userFilters.searchTerm.toLowerCase();
        return user.name.toLowerCase().includes(searchLower) ||
               (user.email ?? '').toLowerCase().includes(searchLower);
      }
      
      return true;
    });
  }

  /**
   * Helper business logic
   */
  private isRecentActivity(lastLogin: string): boolean {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return new Date(lastLogin) > thirtyDaysAgo;
  }

  /**
   * Permission checking business logic
   */
  canUserAccessSchool(user: any, schoolId: string): boolean {
    const userRoleId = (this.rootStore as any)?.userStore?.userData?.personalInfo?.role?.id;
    
    // Business rules
    if (userRoleId === ROLES.ADMIN) return true;
    if (userRoleId === ROLES.PRINCIPAL) {
      return user.school_id === schoolId;
    }
    
    return false;
  }

  // ==================== LEGACY DATA FETCHING METHODS ====================
  // These methods are being phased out in favor of TanStack Query hooks
  // Keep for backward compatibility during migration
  
  /**
   * @deprecated Use useAdminUsers TanStack Query hook instead
   * Load users list - kept for backward compatibility
   */
  loadUsers = async (): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await getUsers(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.users = data.users || [];
      });
    } catch (error: any) {
      console.error("Error loading users:", error);
      this.setError(
        "An error occurred while loading the users list. Please try again later."
      );
    } finally {
      this.setIsLoading(false);
    }
  };

  /**
   * @deprecated Use useAdminUser TanStack Query hook instead
   * Load single user by ID - kept for backward compatibility
   */
  loadUser = async (userId: string): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await getUserById(userId);
      runInAction(() => {
        this.currentUser = data.user || null;
      });
    } catch (error: any) {
      console.error("Error loading user details:", error);
      this.setError("An error occurred while loading user details. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  /**
   * @deprecated Use useCreateAdminUser TanStack Query mutation instead
   * Create new user - kept for backward compatibility
   */
  createNewUser = async (userData: any): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await createUser(userData);
      runInAction(() => {
        this.users = [...this.users, data.user];
      });
      
      // Refresh class data after user creation to show changes in class management page
      await this.loadClasses();
    } catch (error: any) {
      console.error("Error creating new user:", error);
      this.setError("An error occurred while creating the new user. Please try again later.");
      throw error;
    } finally {
      this.setIsLoading(false);
    }
  };

  updateExistingUser = async (userId: string, userData: any): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      
      const data = await updateUser(userId, userData);
      
      runInAction(() => {
        // Full user update - complete replacement instead of merge
        this.users = this.users.map((user) => {
          const isMatch = user.id === userId || 
                         user.id === Number(userId) || 
                         String(user.id) === userId;
          
          if (isMatch) {
            return { 
              ...data.user,
              id: user.id // Preserve original ID to prevent type issues
            };
          }
          return user;
        });
        
        if (this.currentUser && (
          this.currentUser.id === userId || 
          this.currentUser.id === Number(userId) || 
          String(this.currentUser.id) === userId
        )) {
          this.currentUser = { ...this.currentUser, ...data.user };
        }
      });
      
      // Refresh class data after user update to show changes in class management page
      await this.loadClasses();
      
    } catch (error: any) {
      console.error("Error updating user:", error);
      this.setError("An error occurred while updating user details. Please try again later.");
      throw error;
    } finally {
      this.setIsLoading(false);
    }
  };

  removeUser = async (
    userId: string,
    forceDelete = false
  ): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);

      await deleteUser(userId, forceDelete);

      runInAction(() => {
        this.users = this.users.filter((user) => user.id !== userId);
        if (this.currentUser && this.currentUser.id === userId) {
          this.currentUser = null;
        }
      });
      
      // Refresh class data after user deletion to show changes in class management page
      await this.loadClasses();
    } catch (error: any) {
      console.error("Error deleting user:", error);

      if (
        error.response?.status === 409 &&
        error.response?.data?.errorType === "foreign_key_constraint"
      ) {
        const foreignKeyError = new Error("FOREIGN_KEY_CONSTRAINT");
        (foreignKeyError as any).response = error.response;
        throw foreignKeyError;
      }

      this.setError("An error occurred while deleting the user. Please try again later.");
      throw error;
    } finally {
      this.setIsLoading(false);
    }
  };

  loadSystemOverview = async (): Promise<void> => {
    try {
      this.startLoading('systemOverview');
      this.setError(null);
      const data = await getSystemOverview(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.systemOverview = data || null;
      });
    } catch (error: any) {
      console.error("Error loading system overview:", error);
      this.setError("An error occurred while loading the system overview. Please try again later.");
    } finally {
      this.stopLoading('systemOverview');
    }
  };

  loadScoreStatistics = async (): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await getScoreStatistics(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.scoreStatistics = data || null;
      });
    } catch (error: any) {
      console.error("Error loading score statistics:", error);
      this.setError("An error occurred while loading score statistics. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  loadUserGrowthData = async (): Promise<void> => {
    try {
      this.startLoading('userGrowthData');
      this.setError(null);
      const data = await getUserGrowthData(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.userGrowthData = data?.userGrowth || null;
      });
    } catch (error: any) {
      console.error("Error loading user growth data:", error);
      this.setError("An error occurred while loading user growth data. Please try again later.");
    } finally {
      this.stopLoading('userGrowthData');
    }
  };

  loadMostActiveEntities = async (): Promise<void> => {
    try {
      this.startLoading('mostActiveEntities');
      this.setError(null);
      const data = await getMostActiveEntities(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.mostActiveEntities = data || null;
      });
    } catch (error: any) {
      console.error("Error loading most active entities:", error);
      this.setError("An error occurred while loading most active entities. Please try again later.");
    } finally {
      this.stopLoading('mostActiveEntities');
    }
  };

  loadActivityMetrics = async (daysAgo: number = 30): Promise<void> => {
    try {
      this.startLoading('activityMetrics');
      this.setError(null);
      const data = await getActivityMetrics(this.selectedSchoolId || undefined, daysAgo);
      runInAction(() => {
        this.activityMetrics = data || null;
      });
    } catch (error: any) {
      console.error("Error loading activity metrics:", error);
      this.setError("An error occurred while loading activity metrics. Please try again later.");
    } finally {
      this.stopLoading('activityMetrics');
    }
  };

  // Load all overview data from single endpoint for smooth transitions
  loadAllOverviewData = async (): Promise<void> => {
    try {
      this.startLoading('overviewData');
      this.setError(null);
      
      // Clear existing data first to prevent showing stale data
      runInAction(() => {
        this.comprehensiveActivityData = null;
      });

      // Get all data from single activity-metrics endpoint
      const data = await getActivityMetrics(this.selectedSchoolId || undefined, 30);
      
      runInAction(() => {
        // Store the raw comprehensive data - let the component handle calculations
        this.comprehensiveActivityData = data || null;
      });
    } catch (error: any) {
      console.error("Error loading overview data:", error);
      this.setError("An error occurred while loading overview data. Please try again later.");
    } finally {
      this.stopLoading('overviewData');
    }
  };

  loadComprehensiveActivityData = async (daysAgo: number = 30): Promise<void> => {
    try {
      this.isLoadingActivityData = true;
      this.setError(null);
      const data = await getActivityMetrics(this.selectedSchoolId || undefined, daysAgo);
      runInAction(() => {
        this.comprehensiveActivityData = data || null;
      });
    } catch (error: any) {
      console.error("Error loading comprehensive activity data:", error);
      this.setError("An error occurred while loading comprehensive activity data. Please try again later.");
    } finally {
      this.isLoadingActivityData = false;
    }
  };

  loadClasses = async (): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await getClasses(this.selectedSchoolId || undefined);
      runInAction(() => {
        this.classes = data || null;
      });
    } catch (error: any) {
      console.error("Error loading class data:", error);
      this.setError("An error occurred while loading class data. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  // ==================== SCHOOL MANAGEMENT ====================
  // Optimized school selection - no API calls, client-side filtering
  
  setSelectedSchool = (schoolId: string | null) => {
    runInAction(() => {
      // Check if user is system admin
      const userRole = (this.rootStore as any)?.userStore?.userData?.personalInfo?.role?.id;
      const isAdmin = userRole === 3; // System Admin
      const isPrincipal = userRole === 4; // School Principal
      
      // System admins can choose between specific schools or all schools
      if (isAdmin) {
        this.selectedSchoolId = schoolId; // null = all schools, ID = specific school
      } else if (isPrincipal) {
        // School principals can choose from their schools or all their schools
        this.selectedSchoolId = schoolId; // null = all their schools, ID = specific school
      } else {
        // Other users cannot change selection
        this.selectedSchoolId = schoolId;
      }
      
      // Clear selections when school changes
      this.clearSelections();
    });
    
    // No need to reload data - TanStack Query handles data fetching
    // OverviewPage filters the existing data client-side based on selectedSchoolId
  };

  loadAvailableSchools = async (): Promise<void> => {
    try {
      this.isLoadingSchools = true;
      this.setError(null);
      
      // Check user type
      const userRole = (this.rootStore as any)?.userStore?.userData?.personalInfo?.role?.id;
      const isAdmin = userRole === 3; // System Admin
      const isPrincipal = userRole === 4; // School Principal
      
      // Get available schools
      const data = await getSchoolsAndGrades();
      runInAction(() => {
        this.availableSchools = data.schools || [];
        
        // Set initial selection based on user type
        if (!this.selectedSchoolId && this.availableSchools.length > 0) {
          if (isAdmin) {
            // System admin starts with "all schools" (null)
            this.selectedSchoolId = null;
          } else if (isPrincipal) {
            // School principal starts with first school in list
            this.selectedSchoolId = this.availableSchools[0].id;
          }
        }
      });
    } catch (error: any) {
      console.error("Error loading schools:", error);
      this.setError(
        "An error occurred while loading the schools list. Please try again later."
      );
    } finally {
      this.isLoadingSchools = false;
    }
  };

  /**
   * @deprecated Use TanStack Query hooks instead
   * Function to load data with school filtering - kept for backward compatibility
   */
  loadFilteredData = async (): Promise<void> => {
    if (this.selectedSchoolId) {
      // Load filtered data for selected school
      await Promise.all([
        this.loadSystemOverview(),
        this.loadMostActiveEntities(),
        this.loadUserGrowthData(),
        this.loadActivityMetrics(),
        this.loadComprehensiveActivityData()
      ]);
    } else {
      // Load general data
      await Promise.all([
        this.loadSystemOverview(),
        this.loadMostActiveEntities(),
        this.loadUserGrowthData(),
        this.loadActivityMetrics(),
        this.loadComprehensiveActivityData()
      ]);
    }
  };
  
  // ==================== DATA ACCESSORS ====================
  // These getters provide convenient access to comprehensive activity data
  
  // Get comprehensive general activity data
  get systemActivityOverview(): ActivityMetrics | null {
    return this.comprehensiveActivityData?.systemActivity || null;
  }
  
  // Get general system overview data
  get systemOverviewData(): SystemOverview | null {
    return this.comprehensiveActivityData?.overview || null;
  }
  
  // Get specific school by ID
  getSchoolById = (schoolId: string): SchoolActivity | null => {
    if (!this.comprehensiveActivityData?.schools) {
      return null;
    }
    return this.comprehensiveActivityData.schools.find(school => school.id === schoolId) || null;
  };
  
  // Get all schools with activity data
  get schoolsWithActivity(): SchoolActivity[] {
    return this.comprehensiveActivityData?.schools || [];
  };
}

export default HybridAdminController;

// Type alias for backward compatibility
export type AdminController = HybridAdminController; 